/**
 * MultiSelectDropdown Component
 *
 * Core UI component for multi-select dropdown with checkboxes.
 * Used as the base for DataTableFacetedFilter and other contexts.
 *
 * Uses Popover + Command pattern for proper search input handling.
 * Figma reference: Salesperson dropdown (node 2904:30068)
 */

import { useState, useCallback, useId, useRef, useMemo, useEffect, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { KbdShortcut } from '@/components/ui/kbd';
import {
  HIDE_NATIVE_SCROLLBAR,
  ScrollbarOverlay,
} from '@/components/ui/scrollbar-overlay';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChevronDownIcon } from '@/react/components/icons/ChevronDownIcon';
import { CloseIcon } from '@icons/CloseIcon';

import { FIELD_SURFACE } from "@/components/ui/field-surface";
const chevronStyle = { width: 14, height: 8 };

// Animated number component for count - slot machine style
function AnimatedNumber({ value }: { value: number }) {
  const prevValue = useRef(value);
  const direction = value > prevValue.current ? 1 : -1;

  useEffect(() => {
    prevValue.current = value;
  }, [value]);

  return (
    <span className="relative inline-flex overflow-hidden h-[1.2em] items-center">
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={value}
          initial={{ y: direction * 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: direction * -20, opacity: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="inline-block"
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export interface SelectOption {
  /** Unique value for the option */
  value: string;
  /** Display label */
  label: string;
}

// Memoized to prevent re-renders when other items change (rerender-memo)
interface DropdownItemProps {
  option: SelectOption;
  isSelected: boolean;
  onSelect: (value: string) => void;
}

const DropdownItem = memo(function DropdownItem({ option, isSelected, onSelect }: DropdownItemProps) {
  return (
    <CommandItem
      value={option.label}
      onSelect={() => onSelect(option.value)}
      className={cn(
        'flex items-center gap-4 w-full p-4',
        'rounded-lg cursor-pointer',
        'font-inter text-[15px] leading-normal',
        // 100ms ease-out, not the 150ms default: hover feedback on a list row is
        // seen dozens of times a session, and Tailwind's default curve eases in
        // at the start — the moment the pointer lands and you're looking.
        'bg-transparent transition-colors duration-100 ease-out outline-none text-left',
        // Figma highlights an active row with dinnerplate-white-light (#F6F2EC).
        'data-[selected=true]:bg-dinnerplate-white-light'
      )}
    >
      {/* stopPropagation stops the click bubbling to CommandItem, which
          would re-fire onSelect and immediately undo the toggle. */}
      <Checkbox
        checked={isSelected}
        onCheckedChange={() => onSelect(option.value)}
        onClick={(e) => e.stopPropagation()}
      />
      <span
        title={option.label}
        className={cn(
          'leading-normal flex-1 min-w-0 truncate transition-colors',
          isSelected ? 'text-off-black' : 'text-grey',
        )}
      >
        {option.label}
      </span>
    </CommandItem>
  );
});

export interface MultiSelectDropdownProps {
  /** Dropdown trigger label */
  title: string;
  /** Available options */
  options: SelectOption[];
  /** Currently selected values */
  selected: string[];
  /** Callback when selection changes */
  onSelectedChange: (selected: string[]) => void;
  /** Allow multiple selections (default: true) */
  multiple?: boolean;
  /** Additional class names for the trigger button */
  className?: string;
  /** Controlled open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Ref for the trigger button */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /**
   * Max height for the options list before scrolling. When omitted, the
   * dropdown grows to fill the viewport space below the trigger.
   */
  maxHeight?: number;
  /** Show search input (default: true when options > 7) */
  showSearch?: boolean;
  /** Placeholder for the search input. Defaults to "Search <title>". */
  searchPlaceholder?: string;
  /** Show selected names summary in the button (default: false) */
  showSummary?: boolean;
  /** Max number of names to show in summary before "+N more" (default: 2) */
  maxVisibleNames?: number;
  /** Fixed width for the dropdown button (prevents layout jitter) */
  width?: number | string;
}

export function MultiSelectDropdown({
  title,
  options,
  selected,
  onSelectedChange,
  multiple = true,
  className,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  triggerRef: externalTriggerRef,
  maxHeight,
  showSearch: showSearchProp,
  searchPlaceholder,
  showSummary = false,
  maxVisibleNames = 2,
  width,
}: MultiSelectDropdownProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const menuId = useId();
  const internalTriggerRef = useRef<HTMLButtonElement>(null);
  const triggerRef = externalTriggerRef || internalTriggerRef;
  const listRef = useRef<HTMLDivElement>(null);

  // Show search by default when there are more than 7 options
  const showSearch = showSearchProp ?? options.length > 7;

  // A Role filter reads "Search role", not "Search name".
  const resolvedSearchPlaceholder =
    searchPlaceholder ?? (title.trim() ? `Search ${title.trim().toLowerCase()}` : 'Search');

  // O(1) lookup for selected items instead of O(n) array.includes()
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Ref to avoid callback recreation when selected changes
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(value);
      } else {
        setInternalOpen(value);
      }
    },
    [isControlled, controlledOnOpenChange]
  );

  // Clear search when dropdown closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  // Bound the panel to the scroll region the trigger lives in, not the
  // viewport — otherwise a long list runs past the end of the content area and
  // underneath the page's sticky footer (e.g. the table's "Lines per page" row).
  const [collisionBoundary, setCollisionBoundary] = useState<HTMLElement | null>(
    null
  );
  useEffect(() => {
    if (!open) return;

    let el = triggerRef.current?.parentElement ?? null;
    while (el) {
      const { overflowY } = getComputedStyle(el);
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      el = el.parentElement;
    }
    setCollisionBoundary(el);
  }, [open, triggerRef]);

  // Note: When showSearch is enabled, cmdk handles filtering via the hidden CommandInput.
  // We still need this for keyboard shortcuts (number keys) which reference by index.
  const filteredOptions = useMemo(() => {
    if (!showSearch || !searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase().trim();
    return options.filter((option) =>
      option.label.toLowerCase().includes(query)
    );
  }, [options, searchQuery, showSearch]);


  // Stable callback - uses ref to avoid recreation when selected changes
  const handleSelect = useCallback(
    (value: string) => {
      const currentSelected = selectedRef.current;
      const isCurrentlySelected = currentSelected.includes(value);

      if (multiple) {
        const newSelected = isCurrentlySelected
          ? currentSelected.filter((v) => v !== value)
          : [...currentSelected, value];
        onSelectedChange(newSelected);
      } else {
        onSelectedChange([value]);
        setOpen(false);
      }
    },
    [multiple, onSelectedChange, setOpen]
  );

  const handleClear = useCallback(() => {
    onSelectedChange([]);
  }, [onSelectedChange]);

  const handleSelectAll = useCallback(() => {
    if (multiple) {
      // Select all filtered options when searching, otherwise all options
      const optionsToSelect = searchQuery.trim() ? filteredOptions : options;
      onSelectedChange(optionsToSelect.map((o) => o.value));
    }
  }, [multiple, options, filteredOptions, searchQuery, onSelectedChange]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Number keys 1-9 toggle options (only for filtered list)
      const num = parseInt(e.key);
      if (num >= 1 && num <= 9 && num <= filteredOptions.length) {
        e.preventDefault();
        e.stopPropagation();
        handleSelect(filteredOptions[num - 1].value);
        return;
      }

      if (!multiple) return;

      // Alt+A select all, Alt+C clear
      if (!e.altKey) return;

      if (e.code === 'KeyA') {
        e.preventDefault();
        e.stopPropagation();
        handleSelectAll();
      } else if (e.code === 'KeyC') {
        e.preventDefault();
        e.stopPropagation();
        handleClear();
      }
    },
    [multiple, filteredOptions, handleSelect, handleSelectAll, handleClear]
  );

  const selectedCount = selected.length;
  const hasSelection = selectedCount > 0;

  // Build map for O(1) label lookup
  const optionsByValue = useMemo(
    () => new Map(options.map((o) => [o.value, o])),
    [options]
  );

  // Generate summary parts and tooltip for selected items
  const { namesText, remainingCount, tooltipText } = useMemo(() => {
    if (!showSummary || selectedCount === 0) {
      return { namesText: null, remainingCount: 0, tooltipText: null };
    }

    const selectedLabels = selected
      .map((v) => optionsByValue.get(v)?.label)
      .filter(Boolean) as string[];

    // Full list for tooltip
    const fullList = selectedLabels.join(', ');

    if (selectedLabels.length <= maxVisibleNames) {
      return { namesText: fullList, remainingCount: 0, tooltipText: null };
    }

    const visible = selectedLabels.slice(0, maxVisibleNames).join(', ');
    const remaining = selectedLabels.length - maxVisibleNames;
    return {
      namesText: visible,
      remainingCount: remaining,
      tooltipText: fullList,
    };
  }, [showSummary, selected, selectedCount, optionsByValue, maxVisibleNames]);

  return (
    <TooltipProvider delayDuration={300}>
      <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button          ref={triggerRef as React.RefObject<HTMLButtonElement>}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-controls={menuId}
          aria-label={hasSelection ? `${title}, ${selectedCount} selected` : title}
          className={cn(
            'justify-between gap-8',
            'min-w-[184px] h-auto px-6 py-4',
            FIELD_SURFACE,
            'font-inter text-[15px] leading-normal font-normal antialiased',
            'hover:border-dinnerplate-white hover:bg-off-white',
            'focus:border-dinnerplate-white focus-visible:border-dinnerplate-white',
            'focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0',
            className
          )}
          style={width ? { width: typeof width === 'number' ? `${width}px` : width } : undefined}
        >
          <span className="flex-1 text-left flex items-center min-w-0">
            <span
              className={cn(
                'min-w-0 truncate font-semibold text-off-black',
                // When showSummary appends names, keep the title fixed
                // so the names span (which has its own truncate) gets the
                // ellipsis instead.
                namesText && 'shrink-0',
              )}
            >
              {title}
            </span>
            <AnimatePresence>
              {namesText && (
                <motion.span
                  key="separator"
                  initial={{ opacity: 0, scaleX: 0 }}
                  animate={{ opacity: 1, scaleX: 1 }}
                  exit={{ opacity: 0, scaleX: 0 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="mx-1.5 text-grey/50 shrink-0 origin-center"
                >
                  |
                </motion.span>
              )}
            </AnimatePresence>
            <AnimatePresence mode="wait">
              {namesText && (
                <motion.span
                  key="names"
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  transition={{ duration: 0.2, ease: 'easeOut', delay: 0.05 }}
                  className="text-off-black truncate"
                >
                  {namesText}
                </motion.span>
              )}
            </AnimatePresence>
            {remainingCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-off-black shrink-0 ml-1 cursor-help inline-flex items-center">
                    <span>+</span>
                    <AnimatedNumber value={remainingCount} />
                    <span>&nbsp;more</span>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {tooltipText}
                </TooltipContent>
              </Tooltip>
            )}
          </span>
          <ChevronDownIcon
            className={cn(
              'shrink-0 text-off-black chevron-motion',
              open && 'rotate-180'
            )}
            style={chevronStyle}
            aria-hidden="true"
          />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        id={menuId}
        align="start"
        sideOffset={6}
        collisionPadding={12}
        collisionBoundary={collisionBoundary ?? undefined}
        className={cn(
          'p-0',
          'bg-off-white border-[0.5px] border-dinnerplate-white rounded-input',
          'shadow-none',
          'flex flex-col'
        )}
        style={{
          width: 'var(--radix-popover-trigger-width)',
          // Grow with content, but never past the viewport edge
          // (available height already accounts for collisionPadding).
          maxHeight: 'var(--radix-popover-content-available-height)',
        }}
        onKeyDown={handleKeyDown}
      >
        <Command
          shouldFilter={showSearch}
          className="bg-transparent flex flex-col flex-1 min-h-0"
        >
          {/* Hidden CommandInput for cmdk filtering + auto-highlight */}
          <div className="sr-only">
            <CommandInput
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
          </div>

          {/* Visible search input.
              shrink-0: the panel is a height-constrained flex column, so
              without it the search bar is the first thing to get squeezed. */}
          {showSearch && (
            <div className="relative shrink-0 bg-off-white border-b border-dinnerplate-white rounded-t-input overflow-hidden">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={resolvedSearchPlaceholder}
                className={cn(
                  'w-full h-[53px] px-6',
                  'bg-transparent border-none',
                  'font-inter text-sm leading-normal text-off-black placeholder:text-grey-light',
                  'focus:outline-none focus:ring-0',
                  'transition-colors'
                )}
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-6 top-1/2 -translate-y-1/2 p-1 text-grey hover:text-off-black transition-colors"
                  aria-label="Clear search"
                >
                  <CloseIcon className="size-3" />
                </button>
              )}
            </div>
          )}

          {/* `relative` anchors the scrollbar overlay; `mr-2` (6px) keeps the
              lane clear of the panel's border and rounded corners, matching
              Figma's 6px-padded scrollbar column. The panel is capped by
              max-height rather than a fixed height, so this column has no free
              space to grow into — the list must stay content-sized and *shrink*
              (`flex-1 min-h-0` on both levels). Giving it `h-full` instead made
              it 2105px tall with nothing to scroll. */}
          <div className="relative flex flex-col flex-1 min-h-0 mr-2">
            <CommandList
              ref={listRef}
              className={cn(
                // pr-6 (18px) = the 12px lane the overlay paints on top, plus
                // Figma's 6px gap between the rows and the scrollbar column.
                // Without it a row's rounded right corner is hidden under the
                // lane's opaque background when it highlights.
                'flex-1 min-h-0 py-2 pl-2 pr-6',
                HIDE_NATIVE_SCROLLBAR
              )}
              // Inline, because cmdk's own `max-h-[300px]` survives the class
              // merge and would otherwise cap the list at 300px.
              style={{ maxHeight: maxHeight ?? 'none' }}
            >
              <CommandEmpty className="px-6 py-3 font-inter text-sm leading-normal text-grey text-center">
                No results found
              </CommandEmpty>

              <CommandGroup className="p-0">
                {filteredOptions.map((option) => (
                  <DropdownItem
                    key={option.value}
                    option={option}
                    isSelected={selectedSet.has(option.value)}
                    onSelect={handleSelect}
                  />
                ))}
              </CommandGroup>
            </CommandList>
            <ScrollbarOverlay targetRef={listRef} />
          </div>

          {multiple && (
            <>
              <CommandSeparator className="shrink-0 bg-dinnerplate-white" />
              <div className="shrink-0 p-2">
                <CommandItem
                  onSelect={() => hasSelection ? handleClear() : handleSelectAll()}
                  className={cn(
                    'group flex items-center justify-between w-full p-4',
                    'rounded-lg cursor-pointer',
                    'font-inter text-[15px] leading-normal font-semibold antialiased',
                    'hover:bg-transparent transition-colors',
                    'outline-none',
                    'data-[selected=true]:bg-transparent'
                  )}
                >
                  <KbdShortcut
                    keys={['⌥', hasSelection ? 'C' : 'A']}
                    className="[&_kbd]:h-8 [&_kbd]:min-w-8 [&_kbd]:w-8 [&_kbd]:rounded-xs [&_kbd]:text-xs"
                  />
                  <span className="relative font-inter text-[15px] font-semibold leading-normal text-off-black">
                    {hasSelection ? 'Clear all' : 'Select all'}
                    <span className="absolute -bottom-px left-0 h-px w-full origin-left scale-x-0 bg-off-black transition-transform duration-150 ease-out group-hover:scale-x-100" />
                  </span>
                </CommandItem>
              </div>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
    </TooltipProvider>
  );
}
